import React, { useState, useMemo, useCallback, useEffect } from "react";
import privateAxios from "../../../../utils/axiosPrivate";
import { useTable, useRowSelect, useFilters, useExpanded } from "react-table";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import { COLUMNS } from "./Columns";
import { Link } from "react-router-dom";
import { showToast, showAlert } from "../../../../utils/utils";
import TableActionBtnGroup from "../tableactionbtngroup/TableActionBtnGroup";
import UserAccountSoftwareListModal from "../../../modals/UserAccountSoftwareListModal";
import "./accountingtable.scss";
import {
  showToastType,
  TOTAL_USERS,
  ICON_URL,
  IS_SUPPORTED
} from "../../../../utils/constants/appConstants";
import {
  accSoftwarePage,
  noDataPresent,
  action,
  alertActions
} from "../../../../locales/en";
import { useDispatch, useSelector } from "react-redux";
import {
  accountsoftwareImageThunk,
  accountsoftwareListThunk
} from "../../../../redux/accountingSoftwareSlice";
import {
  getActiveAccSoftwareURL,
  getDeactiveAccSoftwareURL
} from "../../../../utils/constants/apiEndPoints";
import TableSkeleton from "../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../hooks/useVisibleColumnCount";
import StatusIcon from "../../../common/StatusIcon";
import ErrorBoundary from "../../../error-boundary";
import ListViewFallback from "../../../error-boundary/fallback/ListViewFallback";
import { ACC_SOFTWARE_TOOLTIP_CONFIG } from "../../../../utils/constants/tooltipsConfig";
import { EDIT_ACCOUNTING_SOFTWARE } from "../../../../route/paths";

const AccSoftwareTable = () => {
  const [selectedRoleId, setSelectedRoleId] = useState();
  const columns = useMemo(() => COLUMNS, []);
  const [data, setData] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [icons, setIcons] = useState({});
  const dispatch = useDispatch();

  const { headerRef, visibleColumnCount } = useVisibleColumnCount();

  const { loading } = useSelector((state) => state.accountingSoftware);

  // Fetch AccountingSoftware Logo
  const fetchReferralImageData = async (rowId) => {
    try {
      const response = await dispatch(
        accountsoftwareImageThunk(rowId)
      ).unwrap();
      const imageUrl = URL.createObjectURL(response.data);

      setIcons((prevIcons) => ({
        ...prevIcons,
        [rowId]: imageUrl
      }));
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.warn(`${accSoftwarePage?.accTable.errMsg.item1} ${rowId}`);
      } else {
        showToast(error.message, showToastType?.error);
      }
    }
  };

  const loadAccouningSoftware = async () => {
    try {
      const response = await dispatch(accountsoftwareListThunk()).unwrap();
      const data = response.data.data;

      setData(data);
      data.forEach((row) => {
        fetchReferralImageData(row.id);
      });
    } catch (error) {
      if (error.response && error.response.status === 500) {
        showToast(error.response.data.errors, showToastType?.error);
      } else {
        showToast(error.message, showToastType?.error);
      }
    }
  };

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable(
      {
        columns,
        data
      },
      useFilters,
      useExpanded,
      useRowSelect
    );

  useEffect(() => {
    loadAccouningSoftware();
  }, []);

  const handleToggleModal = useCallback(
    (rowId) => {
      setSelectedRoleId(rowId);
      setShowModal(!showModal);
    },
    [showModal]
  );

  const updateRowStatus = (rowId, newStatus) => {
    setData((prevData) =>
      prevData.map((row) =>
        row.id === rowId ? { ...row, isSupported: newStatus } : row
      )
    );
  };

  const handleDeactiveUserClick = async (rowId) => {
    try {
      const result = await showAlert(
        accSoftwarePage.accTable.errMsg.item2,
        accSoftwarePage.accTable.errMsg.item3,
        alertActions.warning,
        alertActions.Confirm,
        alertActions.Cancel
      );

      if (result.isConfirmed) {
        const response = await privateAxios.get(
          getDeactiveAccSoftwareURL(rowId)
        );
        const { message } = response.data;
        if (response.status === 200) {
          updateRowStatus(rowId, false);
        }
        showToast(message, showToastType?.success);
      }
    } catch (error) {
      if (error.response.status === 400) {
        showToast(error.response.data.errors, showToastType?.error);
      } else {
        showToast(error.message, showToastType?.error);
      }
    }
  };

  const handleActivateUserClick = async (rowId) => {
    try {
      const response = await privateAxios.get(getActiveAccSoftwareURL(rowId));
      const { message } = response.data;
      if (response.status === 200) {
        updateRowStatus(rowId, true);
      }
      showToast(message, showToastType?.success);
    } catch (error) {
      showToast(error.response.data.errors, showToastType?.error);
    }
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prevState) =>
      prevState.includes(rowId)
        ? prevState.filter((id) => id !== rowId)
        : [...prevState, rowId]
    );
  };

  const centerAlignedColumns = [TOTAL_USERS, ICON_URL, IS_SUPPORTED];

  const renderIcon = (rowId) =>
    icons[rowId] ? (
      <div className="accounting_icon">
        <img src={icons[rowId]} alt="logo" />
      </div>
    ) : (
      <span>-</span>
    );

  const renderCell = (cell, row) => {
    if (cell.column.id === TOTAL_USERS && row.original.totalUsers > 0) {
      return (
        <Link to="" onClick={() => handleToggleModal(row.original.id)}>
          {cell.value}
        </Link>
      );
    }
    if (cell.column.id === ICON_URL) {
      return renderIcon(row.original.id);
    }
    if (cell.column.id === IS_SUPPORTED) {
      return <StatusIcon value={row.original.isSupported} />;
    }
    return cell.render("Cell");
  };

  const renderExpandedCell = (accessor, cellValue, row) => {
    if (accessor === TOTAL_USERS && cellValue > 0) {
      return (
        <Link to="" onClick={() => handleToggleModal(row.original.id)}>
          {cellValue}
        </Link>
      );
    }
    if (accessor === ICON_URL) {
      return renderIcon(row.original.id);
    }
    if (accessor === IS_SUPPORTED) {
      return <StatusIcon value={cellValue} />;
    }
    return String(cellValue);
  };

  return (
    <>
      <ErrorBoundary fallback={<ListViewFallback />}>
        <table
          className="mt-2 mb-3 salesleadtable bgless_table accountingTable"
          {...getTableProps()}
        >
          <thead ref={headerRef}>
            {headerGroups.map((headerGroup) => {
              const { key, ...rest } = headerGroup.getHeaderGroupProps();
              return (
                <tr className="parentrow" {...rest} key={key}>
                  <th />
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps()}
                      style={
                        centerAlignedColumns.includes(column.id)
                          ? { textAlign: "center" }
                          : {}
                      }
                      key={column.id}
                    >
                      <span>{column.render("Header")}</span>
                    </th>
                  ))}
                  <th className="text-center">{action}</th>
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {loading && (
              <TableSkeleton rows={10} columns={visibleColumnCount} />
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + 2} className="text-center">
                  {noDataPresent}
                </td>
              </tr>
            )}
            {!loading &&
              rows.length > 0 &&
              rows.map((row) => {
                prepareRow(row);
                const isExpanded = expandedRows.includes(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <tr className="parentrow" {...row.getRowProps()}>
                      <td className="expand-td">
                        <span
                          className="expandbtn cursor-pointer"
                          onClick={() => toggleRow(row.id)}
                        >
                          {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                        </span>
                      </td>
                      {row.cells.map((cell) => (
                        <td
                          key={cell.getCellProps().key}
                          {...cell.getCellProps()}
                          style={
                            cell.column.id === TOTAL_USERS ||
                            cell.column.id === ICON_URL ||
                            cell.column.id === IS_SUPPORTED
                              ? { textAlign: "center" }
                              : {}
                          }
                        >
                          {renderCell(cell, row)}
                        </td>
                      ))}
                      <td>
                        <TableActionBtnGroup
                          editUrl={EDIT_ACCOUNTING_SOFTWARE(row.original.id)}
                          {...(row.original.isSupported
                            ? {
                                deactivateUser: () =>
                                  handleDeactiveUserClick(row.original.id)
                              }
                            : {
                                activateUser: () =>
                                  handleActivateUserClick(row.original.id)
                              })}
                          tooltips={{
                            ...ACC_SOFTWARE_TOOLTIP_CONFIG,
                            activateUser: {
                              ...ACC_SOFTWARE_TOOLTIP_CONFIG.activateUser,
                              content: row.original.isSupported
                                ? ""
                                : ACC_SOFTWARE_TOOLTIP_CONFIG.activateUser
                                    .content
                            },
                            deactivateUser: {
                              ...ACC_SOFTWARE_TOOLTIP_CONFIG.deactivateUser,
                              content: row.original.isSupported
                                ? ACC_SOFTWARE_TOOLTIP_CONFIG.deactivateUser
                                    .content
                                : ""
                            }
                          }}
                        />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="childrow colexpand">
                        <td colSpan={columns.length + 2}>
                          <div className="tdbody">
                            {columns.map((column) => {
                              const accessor = column.accessor;
                              const cellValue = row.original[accessor];

                              return (
                                <span key={accessor} data-label={column.Header}>
                                  {renderExpandedCell(accessor, cellValue, row)}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </ErrorBoundary>
      <UserAccountSoftwareListModal
        isOpen={showModal}
        toggle={handleToggleModal}
        roleId={selectedRoleId}
      />
    </>
  );
};

export default AccSoftwareTable;
